import { forwardRef, RefObject, useEffect, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Conversation } from '@/components/Conversation';
import useConversationMessage from '@/hooks/useConversationMessage';
import { ChatMetadataParams, useChatMetadata } from '@/hooks/useChatMetadata';
import useCookies from '@/hooks/useCookies';

const PlaygroundConversation = forwardRef(
  (
    {
      newMessageScrollTarget,
      className,
    }: {
      newMessageScrollTarget?: RefObject<HTMLDivElement>;
      className?: string;
    },
    ref,
  ) => {
    const { sessionID, setSessionID } = useCookies();
    const { id } = useParams();

    const chatParams: ChatMetadataParams = {
      chatId: id!,
      sessionID,
    };

    const { data: chatMetadata, refetch } = useChatMetadata(chatParams);

    const {
      loading,
      deleteMessage,
      sendEditorMessage,
      initChat,
      setLoading,
      messages,
      setMessages,
    } = useConversationMessage(chatParams);

    useEffect(() => {
      if (!chatMetadata) return;
      initChat(chatMetadata);
    }, [chatMetadata, sessionID, id]);

    useEffect(() => {
      refetch();
    }, [sessionID, id]);

    async function restartConversation() {
      setMessages([]);
      setLoading(false);
      setSessionID(uuidv4());
    }

    useImperativeHandle(ref, () => ({
      restartConversation,
    }));

    if (!chatMetadata) {
      return null;
    }

    return (
      <Conversation
        newMessageScrollTarget={newMessageScrollTarget}
        className={className}
        chatMetadata={chatMetadata}
        messages={messages}
        loading={loading}
        deleteMessage={deleteMessage}
        forceHideCredit
        onSend={sendEditorMessage}
      />
    );
  },
);

PlaygroundConversation.displayName = 'PlaygroundConversation';

export { PlaygroundConversation };
